import { useIsMobileLayout } from '@src/hooks';
import {
  FormControlSizes,
  LoaderSizes,
  LoaderTalos,
  MixpanelEventSource,
  MixpanelSourceProvider,
  Tour,
  useCallbackRef,
} from '@talos/kyoko';
import { memo, useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { TourName } from '../../tokens/tours';
import { MarketSelectorContextProvider, useMarketSelectorContext } from './MarketSelectorContext';
import { MarketSelectorPanel } from './MarketSelectorPanel';
import { MarketSelectorSelectionsList } from './MarketSelectorSelectionsList';
import { MarketSelectorTrigger } from './MarketSelectorTrigger';
import { MobileMarketSelector } from './mobile/MobileMarketSelector';
import { NarrowMarketSelectorPanel } from './NarrowMarketSelectorPanel';
import { MarketSelectorWrapper } from './styles';
import type { MarketSelectorProps } from './types';
import { useMarketSelectorPanel } from './useMarketSelectorPanel';

export const InnerMarketSelector = ({
  disabled: inputDisabled,
  panelAnchorRef,
  panelPlacement,
  panelOffset,
  forceOpen,
  onOpen,
  placeholder = 'Select Aggregation...',
  selectionsListPlaceholder,
  size = FormControlSizes.Default,
}: MarketSelectorProps) => {
  const { ready: marketSelectorReady, isOpen, setIsOpen, isNarrow } = useMarketSelectorContext();

  // On mobile, every control should have a touch-friendly minimum size, regardless of what the caller requested.
  const isMobileLayout = useIsMobileLayout();
  const effectiveSize = isMobileLayout ? FormControlSizes.Default : size;

  const close = useCallback(() => setIsOpen(false), [setIsOpen]);
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!isOpen || event.key !== 'Escape') {
        return;
      }

      close();
      event.preventDefault();
      event.stopPropagation();
    },
    [close, isOpen]
  );

  const defaultAnchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (forceOpen) {
      setIsOpen(true);
    }
  }, [forceOpen, setIsOpen]);

  const dropdownProps = useMarketSelectorPanel({
    isOpen,
    close,
    panelOffset: panelOffset ?? 0,
    panelPlacement: panelPlacement ?? 'auto',
    panelAnchorRef: panelAnchorRef ?? defaultAnchorRef,
  });

  const combinedDisabled = inputDisabled || !marketSelectorReady;

  const handleOpen = useCallbackRef(() => {
    if (combinedDisabled) {
      return;
    }

    setIsOpen(true);
    if (!isOpen) {
      onOpen?.();
    }
  });

  // Keep track of if the panel has ever been opened. If so, we will render the panel, and keep it rendered despite closing it.
  // This is for performance reasons on-mount.
  const [hasBeenOpenedOnce, setHasBeenOpenedOnce] = useState(false);
  useEffect(() => {
    setHasBeenOpenedOnce(curr => curr || dropdownProps.isOpen);
  }, [dropdownProps.isOpen]);

  if (!marketSelectorReady) {
    return <LoaderTalos size={LoaderSizes.SMALL} />;
  }

  return (
    <MarketSelectorWrapper data-testid="market-selector" ref={defaultAnchorRef} onKeyDown={handleKeyDown}>
      {/* Tour for market selector functionality */}
      <Tour tour={TourName.v253MarketSelector} />

      <MarketSelectorTrigger
        disabled={combinedDisabled}
        onOpen={handleOpen}
        placeholder={placeholder}
        size={effectiveSize}
      />

      {isNarrow ? (
        <NarrowMarketSelectorPanel dropdownProps={dropdownProps} />
      ) : (
        /* For the normal market selector (not popout related), only mount the panel after we have been opened the first time */
        hasBeenOpenedOnce && <MarketSelectorPanel dropdownProps={dropdownProps} />
      )}

      <MarketSelectorSelectionsList placeholder={selectionsListPlaceholder} />
    </MarketSelectorWrapper>
  );
};

/**
 * Render a MarketSelector. Requires props received from the useMarketSelector hook.
 *
 * This MarketSelector renders a BaseSelect with a MarketSelectorBlotter in a subsequent popup/dropdown.
 *
 * This MarketSelector component is just one of many possible view implementations of the market selector state and api.
 */
export const MarketSelector = memo((props: MarketSelectorProps) => {
  const isMobileLayout = useIsMobileLayout();

  if (isMobileLayout) {
    return <MobileMarketSelector {...props} />;
  }

  return (
    <MarketSelectorContextProvider
      isNarrow={!!props.isNarrow}
      marketSelectorAPI={props.marketSelectorAPI}
      showCredentialsOnlyToggle={props.showCredentialsOnlyToggle}
      disableAggregations={props.disableAggregations}
      extensions={props.extensions}
    >
      <MixpanelSourceProvider value={MixpanelEventSource.MarketAccountSelector}>
        <InnerMarketSelector {...props} />
      </MixpanelSourceProvider>
    </MarketSelectorContextProvider>
  );
});
